/**
 * Irmão: `src/app/components/filters/FilterRail.tsx`.
 *
 * Duplicação deliberada (openspec/changes/flashcards-revisao-redesign/design.md,
 * Decisão 2). Não reusar `components/filters/`: store, persistência e efeito de
 * aplicação são de outra tela. Uma correção visual aqui deve ser conferida
 * no irmão, e o inverso.
 *
 * A contagem é sempre `escolhidos/disponíveis` (`1/6`, e também `0/7`). O irmão
 * omite o numerador quando nada está escolhido; aqui o formato com barra fica,
 * porque a dimensão tem de dizer quantas opções estão escolhidas sobre quantas
 * existem sem a seção ser aberta (revisao-home, "A contagem de uma dimensão é
 * visível sem abri-la").
 */
import { ReactNode } from "react";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "./tokens";

export interface RevisaoFilterRailEntry {
  id: string;
  label: string;
  icon: ReactNode;
  /** Options that exist in this dimension. */
  total: number;
  /** Options currently chosen. */
  selected: number;
}

export interface RevisaoFilterRailProps {
  entries: RevisaoFilterRailEntry[];
  activeId: string;
  onSelect: (id: string) => void;
  className?: string;
}

export default function RevisaoFilterRail({
  entries,
  activeId,
  onSelect,
  className,
}: RevisaoFilterRailProps) {
  return (
    <nav
      className={revisaoFilterCx("flex flex-col gap-0.5", className)}
      aria-label="Dimensões da revisão"
    >
      {entries.map((entry) => {
        const active = entry.id === activeId;
        const hasSelection = entry.selected > 0;

        return (
          <button
            key={entry.id}
            type="button"
            onClick={() => onSelect(entry.id)}
            aria-current={active ? "true" : undefined}
            className={revisaoFilterCx(
              "flex items-center gap-2.5 rounded-chip px-3 py-2 text-left text-[13px]",
              "transition-colors duration-150",
              "[&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0",
              REVISAO_FILTER_FOCUS_RING,
              active
                ? "bg-select/[0.12] font-semibold text-select-ink"
                : "font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink",
            )}
          >
            {entry.icon}
            <span className="min-w-0 flex-1 truncate">{entry.label}</span>
            <span
              className={revisaoFilterCx(
                "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
                hasSelection
                  ? "bg-select/[0.14] text-select-ink"
                  : "bg-surface-muted text-ink-muted",
              )}
              aria-label={`${entry.selected} de ${entry.total} selecionados`}
            >
              {`${entry.selected}/${entry.total}`}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
